"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Layers, Search, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  getPublicIntegrations,
  type PublicIntegrationCard,
  type PublicIntegrationCategory,
  resolvePublicAssetUrl,
} from "@/lib/api";

function useDebouncedValue(value: string, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function IntegrationLogo({ integration }: { integration: PublicIntegrationCard }) {
  const logo = integration.logo ? resolvePublicAssetUrl(integration.logo) : "";

  if (logo) {
    return <img src={logo} alt={`${integration.name} logo`} className="h-8 w-8 object-contain" loading="lazy" />;
  }

  return <Layers className="h-5 w-5 text-slate-600 dark:text-slate-300" />;
}

export default function IntegrationsCatalog() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] = useState<PublicIntegrationCard[]>([]);
  const [categories, setCategories] = useState<PublicIntegrationCategory[]>([]);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    getPublicIntegrations({
      search: debouncedQuery || undefined,
      category: selectedCategory !== "all" ? selectedCategory : undefined,
      page,
      limit: 24,
    })
      .then((response) => {
        if (!active) return;
        setIntegrations(response.integrations || []);
        setCategories(response.categories || []);
        setTotalPages(response.pagination?.totalPages || 1);
      })
      .catch(() => {
        if (!active) return;
        setError("Unable to load integrations right now.");
        setIntegrations([]);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [debouncedQuery, selectedCategory, page]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, selectedCategory]);

  const featured = useMemo(() => integrations.filter((item) => item.isFeatured).slice(0, 8), [integrations]);

  return (
    <div className="space-y-10">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-6 shadow-xl shadow-slate-900/5 dark:border-slate-800 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900/80">
        <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-48 w-48 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative grid gap-5 lg:grid-cols-[2fr_1fr] lg:items-end">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700 ring-1 ring-sky-200 dark:bg-slate-900/70 dark:text-sky-300 dark:ring-slate-700">
              <Sparkles className="h-3.5 w-3.5" /> Integrations
            </p>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-white lg:text-5xl">
              Connect NotifyStudio with your complete growth stack
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300 lg:text-lg">
              Browse premium integrations across CRM, e-commerce, analytics, automation, and engagement platforms. Launch faster with setup guides and proven use cases.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200/70 bg-white/70 p-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/60">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Catalog Scale</p>
            <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">100+</p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Popular platforms ready for conversion workflows.</p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 rounded-3xl border border-slate-200/70 bg-white/80 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/80 lg:grid-cols-[1.2fr_2fr]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search integrations..."
            className="h-11 pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className={`rounded-full px-3 py-2 text-xs font-semibold transition ${selectedCategory === "all" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"}`}
            onClick={() => setSelectedCategory("all")}
          >
            All Categories
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`rounded-full px-3 py-2 text-xs font-semibold transition ${selectedCategory === category.slug ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"}`}
              onClick={() => setSelectedCategory(category.slug)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </section>

      {featured.length > 0 ? (
        <section>
          <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">Featured Integrations</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {featured.map((integration) => (
              <Link
                key={`featured-${integration.id}`}
                href={`/integrations/${integration.slug}`}
                className="group rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm transition hover:-translate-y-1 hover:border-sky-300 hover:shadow-lg dark:border-slate-800 dark:bg-slate-950"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-900">
                  <IntegrationLogo integration={integration} />
                </div>
                <p className="text-sm font-semibold text-slate-900 group-hover:text-sky-600 dark:text-white dark:group-hover:text-sky-300">
                  {integration.name}
                </p>
                <p className="mt-1 line-clamp-2 text-xs leading-6 text-slate-500 dark:text-slate-400">
                  {integration.shortDescription}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-white">All Integrations</h2>
        {loading ? <p className="text-sm text-slate-500">Loading integrations...</p> : null}
        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        {!loading && !error && integrations.length === 0 ? (
          <p className="text-sm text-slate-500">No integrations matched your filters.</p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {integrations.map((integration) => (
            <Link
              key={integration.id}
              href={`/integrations/${integration.slug}`}
              className="group h-full"
            >
              <Card className="h-full overflow-hidden border-slate-200/70 bg-white/95 transition hover:-translate-y-1 hover:border-sky-300 hover:shadow-2xl dark:border-slate-800 dark:bg-slate-950/90">
                <CardContent className="p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-900">
                      <IntegrationLogo integration={integration} />
                    </div>
                    {integration.category ? <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600 dark:bg-slate-900 dark:text-slate-300">{integration.category}</span> : null}
                  </div>
                  <h3 className="text-base font-semibold text-slate-900 transition group-hover:text-sky-600 dark:text-white dark:group-hover:text-sky-300">{integration.name}</h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-7 text-slate-500 dark:text-slate-400">{integration.shortDescription}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>{integration.setupTime || "~15 min setup"}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-sky-600 dark:text-sky-300">
                      View guide <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>
            Previous
          </Button>
          <span className="px-3 text-sm text-slate-500 dark:text-slate-400">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages}>
            Next
          </Button>
        </div>
      </section>
    </div>
  );
}
